import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { casesAPI, clientsAPI, usersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, 
  Search, 
  Filter,
  Edit,
  Trash2,
  Eye,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const CasesPage = () => {
  const { t, language } = useLanguage();
  const { canManageCases, isAdmin, user } = useAuth();
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [viewingCase, setViewingCase] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    case_number: '',
    client_id: '',
    assigned_lawyer_id: '',
    case_type: '',
    status: 'active',
    court_name: '',
    description: '',
    notes: ''
  });

  const caseTypes = [
    { value: 'civil', label: language === 'ar' ? 'مدني' : 'Civil' },
    { value: 'criminal', label: language === 'ar' ? 'جنائي' : 'Criminal' },
    { value: 'commercial', label: language === 'ar' ? 'تجاري' : 'Commercial' },
    { value: 'family', label: language === 'ar' ? 'أسري' : 'Family' },
    { value: 'labor', label: language === 'ar' ? 'عمالي' : 'Labor' },
    { value: 'administrative', label: language === 'ar' ? 'إداري' : 'Administrative' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [casesRes, clientsRes, usersRes] = await Promise.all([
        casesAPI.getAll(),
        clientsAPI.getAll(),
        usersAPI.getAll()
      ]);
      
      // Filter cases for lawyers - they only see their assigned cases
      let filteredCases = casesRes.data;
      if (!isAdmin && user?.role === 'lawyer') {
        filteredCases = casesRes.data.filter(c => c.assigned_lawyer_id === user.id);
      }
      
      setCases(filteredCases);
      setClients(clientsRes.data);
      setLawyers(usersRes.data.filter(u => u.role === 'lawyer' || u.role === 'admin'));
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCase) {
        await casesAPI.update(editingCase.id, formData);
        toast.success(language === 'ar' ? 'تم تحديث القضية' : 'Case updated');
      } else {
        await casesAPI.create(formData);
        toast.success(language === 'ar' ? 'تم إنشاء القضية' : 'Case created');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(t('error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد؟' : 'Are you sure?')) return;
    try {
      await casesAPI.delete(id);
      toast.success(language === 'ar' ? 'تم حذف القضية' : 'Case deleted');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t('error'));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      case_number: '',
      client_id: '',
      assigned_lawyer_id: '',
      case_type: '',
      status: 'active',
      court_name: '',
      description: '',
      notes: ''
    });
    setEditingCase(null);
  };

  const openEditDialog = (caseItem) => {
    setEditingCase(caseItem);
    setFormData({
      title: caseItem.title,
      case_number: caseItem.case_number,
      client_id: caseItem.client_id,
      assigned_lawyer_id: caseItem.assigned_lawyer_id || '',
      case_type: caseItem.case_type,
      status: caseItem.status,
      court_name: caseItem.court_name || '',
      description: caseItem.description || '',
      notes: caseItem.notes || ''
    });
    setDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { class: 'status-active', label: t('active') },
      pending: { class: 'status-pending', label: t('pending') },
      closed: { class: 'status-closed', label: t('closed') },
      archived: { class: 'status-archived', label: t('archived') }
    };
    const s = statusMap[status] || statusMap.active;
    return <Badge className={s.class}>{s.label}</Badge>;
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.full_name || '-';
  };

  const getLawyerName = (lawyerId) => {
    const lawyer = lawyers.find(l => l.id === lawyerId);
    return lawyer?.full_name || '-';
  };

  const filteredCases = cases.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         c.case_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="cases-page">
      <Header title={t('cases')} />
      
      <div className="p-6 space-y-6">
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-cases"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]" data-testid="filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="active">{t('active')}</SelectItem>
                <SelectItem value="pending">{t('pending')}</SelectItem>
                <SelectItem value="closed">{t('closed')}</SelectItem>
                <SelectItem value="archived">{t('archived')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {canManageCases && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="btn-accent gap-2" data-testid="create-case-btn">
                  <Plus className="w-4 h-4" />
                  {t('createCase')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingCase ? t('editCase') : t('createCase')}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('caseNumber')} *</Label>
                      <Input
                        value={formData.case_number}
                        onChange={(e) => setFormData({...formData, case_number: e.target.value})}
                        required
                        data-testid="case-number-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('caseTitle')} *</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        required
                        data-testid="case-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('client')} *</Label>
                      <Select 
                        value={formData.client_id} 
                        onValueChange={(v) => setFormData({...formData, client_id: v})}
                      >
                        <SelectTrigger data-testid="case-client-select">
                          <SelectValue placeholder={t('client')} />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map(client => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.full_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('assignedLawyer')}</Label>
                      <Select 
                        value={formData.assigned_lawyer_id || "none"} 
                        onValueChange={(v) => setFormData({...formData, assigned_lawyer_id: v === "none" ? "" : v})}
                      >
                        <SelectTrigger data-testid="case-lawyer-select">
                          <SelectValue placeholder={t('assignedLawyer')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{language === 'ar' ? 'غير محدد' : 'Not assigned'}</SelectItem>
                          {lawyers.map(lawyer => (
                            <SelectItem key={lawyer.id} value={lawyer.id}>
                              {lawyer.full_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('caseType')} *</Label>
                      <Select 
                        value={formData.case_type} 
                        onValueChange={(v) => setFormData({...formData, case_type: v})}
                      >
                        <SelectTrigger data-testid="case-type-select">
                          <SelectValue placeholder={t('caseType')} />
                        </SelectTrigger>
                        <SelectContent>
                          {caseTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('caseStatus')}</Label>
                      <Select 
                        value={formData.status} 
                        onValueChange={(v) => setFormData({...formData, status: v})}
                      >
                        <SelectTrigger data-testid="case-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">{t('active')}</SelectItem>
                          <SelectItem value="pending">{t('pending')}</SelectItem>
                          <SelectItem value="closed">{t('closed')}</SelectItem>
                          <SelectItem value="archived">{t('archived')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>{t('courtName')}</Label>
                      <Input
                        value={formData.court_name}
                        onChange={(e) => setFormData({...formData, court_name: e.target.value})}
                        data-testid="case-court-input"
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>{t('description')}</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        rows={3}
                        data-testid="case-description-input"
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>{t('notes')}</Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        rows={2}
                        data-testid="case-notes-input"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button type="submit" className="btn-accent" data-testid="case-submit-btn">
                      {t('save')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Cases Grid */}
        {filteredCases.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCases.map((caseItem) => (
              <Card 
                key={caseItem.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                data-testid={`case-card-${caseItem.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-sm bg-primary/10">
                        <Briefcase className="w-5 h-5 text-primary" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-xs font-mono text-muted-foreground">{caseItem.case_number}</p>
                        <CardTitle className="text-base mt-0.5">{caseItem.title}</CardTitle>
                      </div>
                    </div>
                    {getStatusBadge(caseItem.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs">{t('client')}</p>
                      <p className="font-medium truncate">{getClientName(caseItem.client_id)}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">{t('caseType')}</p>
                      <p className="font-medium capitalize">{caseItem.case_type}</p>
                    </div>
                  </div>
                  {caseItem.court_name && (
                    <div className="text-sm">
                      <p className="text-muted-foreground text-xs">{t('courtName')}</p>
                      <p className="font-medium truncate">{caseItem.court_name}</p>
                    </div>
                  )}
                  
                  {canManageCases && (
                    <div className="flex items-center gap-2 pt-3 border-t border-border">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => setViewingCase(caseItem)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {t('view')}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => openEditDialog(caseItem)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        {t('edit')}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(caseItem.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <Briefcase className="w-12 h-12 mx-auto text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">{t('noData')}</p>
          </Card>
        )}

        {/* View Case Dialog */}
        <Dialog open={!!viewingCase} onOpenChange={() => setViewingCase(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('caseDetails')}</DialogTitle>
            </DialogHeader>
            {viewingCase && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('caseNumber')}</p>
                    <p className="font-mono font-medium">{viewingCase.case_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('caseStatus')}</p>
                    {getStatusBadge(viewingCase.status)}
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-muted-foreground">{t('caseTitle')}</p>
                    <p className="font-medium">{viewingCase.title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('client')}</p>
                    <p className="font-medium">{getClientName(viewingCase.client_id)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('assignedLawyer')}</p>
                    <p className="font-medium">{getLawyerName(viewingCase.assigned_lawyer_id)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('caseType')}</p>
                    <p className="font-medium capitalize">{viewingCase.case_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('courtName')}</p>
                    <p className="font-medium">{viewingCase.court_name || '-'}</p>
                  </div>
                  {viewingCase.description && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">{t('description')}</p>
                      <p className="font-medium">{viewingCase.description}</p>
                    </div>
                  )}
                  {viewingCase.notes && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">{t('notes')}</p>
                      <p className="font-medium">{viewingCase.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CasesPage;
